import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { dashboardComponentChangeLog } from 'components/DashboardComponent/utils'
import { DashBuilderStatus, useRealTime } from 'contexts/RealTimeContext'
import { useDashboardItemsWithQueriesLoaded, useDebounce, useIsMobileDevice, useOutsideClick } from 'hooks'

import type { IComponentProps } from '../common/dashboard.types'
import { getTextContent } from '../common/dashboard.utils'

export const useTextComponentModel = ({
  body,
  item,
  editComponent,
  dashboardJobId,
  gridRef,
  isDragDisabled,
  isViewMode,
}: IComponentProps) => {
  const [textContent, setTextContent] = useState(getTextContent(body?.textContent))
  const [hasFocus, setHasFocus] = useState(false)
  const componentRef = useRef<HTMLDivElement>(null)
  const { dashBuilderStatus } = useRealTime()

  const isMobile = useIsMobileDevice()

  const isGeneratingBriefing = dashBuilderStatus === DashBuilderStatus.Pending

  const isEmpty = useMemo(() => {
    const parsedDocument = new DOMParser().parseFromString(textContent, 'text/html')

    const textValue = parsedDocument.body.textContent ?? ''
    const textWithoutLineFeed = textValue.replace('\n', '')
    const textWithoutSpace = textValue.replace(' ', '')

    return textWithoutLineFeed.length <= 0 || textWithoutSpace.length <= 0
  }, [textContent])

  const isLoading = isEmpty && isGeneratingBriefing

  const { isClickOutside } = useOutsideClick(componentRef)

  useDashboardItemsWithQueriesLoaded({
    id: item.id,
    type: item.type,
    isLoaded: true,
    body,
    headers: item.headers,
  })

  const handleSaveText = useCallback(
    value => {
      if (isDragDisabled) {
        return
      }
      const newTextItem = {
        ...item,
        body: {
          ...item?.body,
          textContent: value,
        },
      }
      editComponent(newTextItem)
      dashboardComponentChangeLog({
        componentType: 'text',
        dashboardId: dashboardJobId,
      })
    },
    [dashboardJobId, editComponent, isDragDisabled, item]
  )

  const { debouncedAction } = useDebounce(handleSaveText)

  const handleTextChange = (value: string) => {
    debouncedAction(value)
  }

  const handleMovable = useCallback(
    (shouldMove: boolean) => {
      const mainGridGridstack = (document.querySelector('.GridstackWrapper') as any)?.gridstack
      mainGridGridstack?.enableMove(shouldMove)

      const itemHTMLElement = document.getElementById(item.id)
      if (itemHTMLElement) {
        gridRef?.movable(itemHTMLElement, shouldMove)
      }
    },
    [gridRef, item.id]
  )

  useEffect(() => {
    if (isDragDisabled || isGeneratingBriefing) {
      return
    }
    if (textContent !== getTextContent(body?.textContent)) {
      setTextContent(getTextContent(body?.textContent))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [body?.textContent, isDragDisabled, isGeneratingBriefing])

  useEffect(() => {
    if (isDragDisabled) {
      return
    }
    if (isClickOutside) {
      setHasFocus(!isClickOutside)
      handleMovable(true)
    }
  }, [handleMovable, isClickOutside, isDragDisabled])

  useEffect(() => {
    if (hasFocus) {
      handleMovable(false)
    }
  }, [handleMovable, hasFocus])

  const textAreaHeight = useMemo(() => {
    const componentHeight = Number(item.headers.h)
    const rowHeight = 48
    const inset = 10
    return componentHeight * rowHeight - inset
  }, [item.headers.h])

  const toolbarAlign: 'left' | 'right' = useMemo(() => {
    if (item.headers?.x >= 6) {
      return 'right'
    }
    return 'left'
  }, [item.headers?.x])

  const shouldBlockEditing = isViewMode || isMobile || isDragDisabled

  const handleClickInDivOverlay = e => {
    if (!shouldBlockEditing) {
      setHasFocus(true)
    }
    // Hacky to make links works in the text component even when the dashboard is not marked as done
    // Temporarily hide the overlay to detect the element underneath
    const overlay = e.currentTarget

    overlay.style.display = 'none'
    requestAnimationFrame(() => {
      // We ensure DOM update and the overlay is hidden before checking the element
      const { clientX, clientY } = e
      const underlyingElement = document.elementFromPoint(clientX, clientY)
      overlay.style.display = ''
      const anchor = underlyingElement?.closest?.('a')
      //Check if the underlying element is an anchor tag, if so, open it
      if (anchor?.href) {
        window.open(anchor.href, anchor.target || '_blank')
        e.stopPropagation()
        e.preventDefault()
      }
    })
  }

  useEffect(() => {
    componentRef.current.style.setProperty('--textHeight', `${textAreaHeight}px`)
  }, [textAreaHeight])

  return {
    textContent,
    hasFocus,
    componentRef,
    handleTextChange,
    handleClickInDivOverlay,
    toolbarAlign,
    isLoading,
    textAreaHeight,
    isViewMode: shouldBlockEditing,
  }
}
